import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { APP_URL, slugifyName } from '../lib/config';

export interface Friend {
  id: string;
  email: string;
  name: string;
  counts: Record<string, number>;
}

export function useFriends(userId: string, userEmail: string, userName: string) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    const { data: friendships } = await supabase
      .from('friendships')
      .select('friend_id, friend_email, friend_name')
      .eq('user_id', userId);

    if (!friendships || friendships.length === 0) {
      setFriends([]);
      setLoading(false);
      return;
    }

    const friendData: Friend[] = await Promise.all(
      friendships.map(async (f: { friend_id: string; friend_email: string; friend_name: string }) => {
        const { data: stickers } = await supabase
          .from('stickers')
          .select('sticker_code, count')
          .eq('user_id', f.friend_id);

        const counts: Record<string, number> = {};
        stickers?.forEach((s: { sticker_code: string; count: number }) => {
          counts[s.sticker_code] = s.count;
        });

        return {
          id: f.friend_id,
          email: f.friend_email,
          name: f.friend_name || f.friend_email,
          counts,
        };
      })
    );

    setFriends(friendData);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const generateInviteLink = useCallback(async (): Promise<string | null> => {
    const { data, error } = await supabase
      .from('friend_invites')
      .insert({ inviter_id: userId, inviter_email: userEmail, inviter_name: userName })
      .select('token')
      .single();

    if (error || !data) return null;
    const slug = slugifyName(userName || userEmail);
    return `${APP_URL}/convite/${slug}/${data.token}`;
  }, [userId, userEmail, userName]);

  const removeFriend = useCallback(
    async (friendId: string) => {
      await supabase
        .from('friendships')
        .delete()
        .eq('user_id', userId)
        .eq('friend_id', friendId);
      setFriends((prev) => prev.filter((f) => f.id !== friendId));
    },
    [userId]
  );

  return { friends, loading, generateInviteLink, removeFriend, refresh: load };
}
